import React from 'react';

import { Listing, SearchView } from '@kamernet/core/ApiClient/Kamernet';
import { useIntl } from '@kamernet/core/Intl';

import { ListingCard } from '@kamernet/modules/Listing/components/ListingCard';
import { SignupCard } from '@kamernet/modules/Listing/components/SignupCard';
import { SearchResultGrid } from '@kamernet/modules/Search/components/SearchResultGrid';

import { ListingsSearchContext } from '../../ListingsSearchContext';

interface GridGeneratorProps {
  listings: Array<Listing>;
  addSignupCard?: boolean;
}

type ListingWithSignup = Listing | { key: 'signup' };

const insertSignupCard = (
  listings: Array<Listing>,
  addSignupCard: boolean,
): Array<ListingWithSignup> => {
  const result: Array<ListingWithSignup> = [...listings];
  if (addSignupCard) {
    if (result.length > 5) {
      result.splice(5, 0, { key: 'signup' });
    } else {
      result.push({ key: 'signup' });
    }
  }
  return result;
};

const GridGeneratorInternal = ({
  listings,
  addSignupCard = false,
}: GridGeneratorProps) => {
  const { allFilters } = ListingsSearchContext.useContext();
  const { T } = useIntl();

  const listingCards = React.useMemo(
    () => insertSignupCard(listings, addSignupCard),
    [listings, addSignupCard],
  );

  return (
    <SearchResultGrid singleColumn={allFilters.searchView === SearchView.Map}>
      {listingCards.map((item, index) => {
        if ('key' in item && item.key === 'signup') {
          const signupKey = `signup-${index}`;
          return (
            <SignupCard
              key={signupKey}
              title={T('search_alerts.search_page.heading.find_faster')}
              subtitle={T('search_alerts.search_page.body.new_ad')}
              buttonMessage={T('search_alerts.search_page.cta.save_search')}
            />
          );
        }

        const listing = item as Listing;

        return (
          <ListingCard
            key={listing.listingId}
            listingId={listing.listingId}
            thumbnailUrl={listing.thumbnailUrl}
            fullPreviewImageUrl={listing.fullPreviewImageUrl}
            isTopAdvert={listing.isTopAdvert}
            furnishingId={listing.furnishingId}
            availabilityStartDate={listing.availabilityStartDate}
            availabilityEndDate={listing.availabilityEndDate}
            streetName={listing.street}
            streetSlug={listing.streetSlug}
            cityName={listing.city}
            citySlug={listing.citySlug}
            isFavorite={listing.isFavorite}
            surfaceArea={listing.surfaceArea}
            listingType={listing.listingType}
            totalRentalPrice={listing.totalRentalPrice}
            utilitiesIncluded={listing.utilitiesIncluded}
            isNewAdvert={listing.isNewAdvert}
            isReactForFree={listing.isReactForFree}
            isStudentHouseAdvert={listing.isStudentHouseAdvert}
          />
        );
      })}
    </SearchResultGrid>
  );
};

export const GridGenerator = React.memo(GridGeneratorInternal);
